import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Box, IconButton, Stack } from '@mui/material';
import { memo } from 'react';

import { type KeyValueFieldNames } from './types';
import { useReactFlowViewContext } from '../../../context/context';
import { BlockValueTemplateInnerField } from '../CodeMirror/FlowTemplate';
import { getCodeMirrorFormFieldSxProps } from '../CodeMirror/defaultProps';
import { type ConfigFieldProps } from '../types';
import { useSelectedNodeField } from '../useSelectedNodeField';

interface BlockKeyValueRowProps extends ConfigFieldProps {
  onDelete: () => void;
  keyValueFieldNames: KeyValueFieldNames;
  autoFocus?: boolean;
  rowIndex: number;
}

function InnerBlockKeyValueRow({
  fieldName,
  onDelete,
  keyValueFieldNames,
  required,
  autoFocus,
  rowIndex,
}: BlockKeyValueRowProps) {
  const keyFieldProps = useSelectedNodeField<string>(`${fieldName}.${keyValueFieldNames.key.name}`);
  const valueFieldProps = useSelectedNodeField<string>(
    `${fieldName}.${keyValueFieldNames.value.name}`
  );
  const isFieldErrorVisible =
    keyFieldProps.isFieldErrorVisible || valueFieldProps.isFieldErrorVisible;
  const { isEditable } = useReactFlowViewContext();

  return (
    <Stack
      direction="row"
      sx={getCodeMirrorFormFieldSxProps({
        isEditable,
        editorElementStyles: {
          backgroundColor: 'transparent',
        },
      })}
      alignItems="center"
      bgcolor={isFieldErrorVisible ? 'rgba(255, 0, 0, 0.1)' : undefined}
      borderTop="1px solid"
      borderColor="rgba(0, 0, 0, 0.23)"
    >
      <Box flex={5} minWidth="0">
        <BlockValueTemplateInnerField
          required={required}
          autoFocus={autoFocus}
          fieldId={`${fieldName}.${keyValueFieldNames.key.name}`}
          fieldName={`${fieldName}.${keyValueFieldNames.key.name}`}
          label={`Row ${rowIndex + 1} ${keyValueFieldNames.key.label}`}
          placeholder={`Add ${keyValueFieldNames.key.label.toLowerCase()}`}
          variant="borderless"
        />
      </Box>
      <Box flex={5} minWidth="0" marginLeft="8px">
        <BlockValueTemplateInnerField
          required={required}
          fieldId={`${fieldName}.${keyValueFieldNames.value.name}`}
          fieldName={`${fieldName}.${keyValueFieldNames.value.name}`}
          label={`Row ${rowIndex + 1} ${keyValueFieldNames.value.label}`}
          placeholder={`Add ${keyValueFieldNames.value.label.toLowerCase()}`}
          variant="borderless"
        />
      </Box>
      <Box flex={1} textAlign="right">
        <IconButton
          onClick={() => onDelete()}
          tabIndex={-1}
          aria-label={`Delete item ${rowIndex + 1}`}
        >
          <DeleteOutlineIcon />
        </IconButton>
      </Box>
    </Stack>
  );
}

export const BlockKeyValueRow = memo(InnerBlockKeyValueRow);
BlockKeyValueRow.displayName = 'BlockKeyValueRow';
